// The player's skin: the colours of its popup menus and of a control-bar
// button whose menu is open. Layout is the same in every skin; only paint
// differs.
//
//   ?skin=mono        (default) today's white-on-dark-grey look
//   ?skin=bluegreen   Gavin's 17 Sep mock: teal-to-olive menus, teal bands on
//                     the selected rows, the open button tinted teal.
//                     `blue`, `green` and `blue-green` are accepted too.
//
// A QUERY STRING ON PURPOSE, not a viewer setting or an admin field (Kai,
// 28 Sep). Installations of this player will likely each fix one skin for
// every video they show, and how an installation says so is not decided yet.
// The query string lets both skins be shown and compared without committing to
// that mechanism; whatever replaces it only has to produce a `Skin`.
//
// Unlike designPreview.ts, this is not a temporary A/B switch: both skins stay.

export type Skin = 'mono' | 'bluegreen'

export interface SkinTokens {
  // The popup slab behind every menu.
  menuBackground: string
  // A selected row (Normal, Medium, the chosen language), and in bluegreen
  // also the on/off rows that head each menu.
  activeRow: string
  // Heading rows — Follow, Transcript, Subtitles. In mono they are ordinary
  // rows; in bluegreen they sit in the same dark band as a selection, as the
  // mock draws them.
  headingRow: string
  // The icon of a bar button while its menu (or, for the V, its window) is
  // open. Gavin, 30 Sep: "the transcript / subtitle button is green when
  // selected - goes for any selected button" — selected meaning the one whose
  // menu is up, so exactly one button is green at a time (Kai, 30 Sep, on the
  // first cut, which had painted the on-state instead).
  openIcon: string
  // Square corners in bluegreen — the mock's slabs have none.
  menuRadius: string
  // Row text. The mock sets menus larger than the mono ones.
  rowFontSize: string
}

// Sampled from Gavin's mock (docs/feedback/2026-09-28): the slab's top and
// bottom edges, the dark band behind Follow / Normal / Medium / German, and
// the eye icon with its menu open.
export const BLUEGREEN_TOP = '#5FB5B2'
export const BLUEGREEN_BOTTOM = '#445E39'
export const BLUEGREEN_BAND = '#006E70'
export const BLUEGREEN_OPEN = '#60B9B5'
// The green square Gavin drew over the subtitle button in his 30 Sep mock.
// The one green for a selected button; the bluegreen skin keeps the teal its
// own mock draws.
// Gavin, 3 Nov: "can you make the green brighter please" — slightly, not by
// much: #009042 → #00A84D, the same hue a few points lighter. It is the one
// green for lit buttons, menu headings and ticks alike.
export const OPEN_GREEN = '#00A84D'

export const SKINS: Record<Skin, SkinTokens> = {
  mono: {
    menuBackground: 'rgba(20,20,20,0.95)',
    activeRow: 'rgba(255,255,255,0.15)',
    headingRow: 'transparent',
    openIcon: OPEN_GREEN,
    menuRadius: '6px',
    rowFontSize: '13px',
  },
  bluegreen: {
    menuBackground: `linear-gradient(to bottom, ${BLUEGREEN_TOP}, ${BLUEGREEN_BOTTOM})`,
    activeRow: BLUEGREEN_BAND,
    headingRow: BLUEGREEN_BAND,
    openIcon: BLUEGREEN_OPEN,
    menuRadius: '0',
    rowFontSize: '15px',
  },
}

export const resolveSkin = (value: string | null): Skin => {
  const v = value?.trim().toLowerCase()
  return v === 'bluegreen' || v === 'blue-green' || v === 'blue' || v === 'green' ? 'bluegreen' : 'mono'
}

// Captured when the module first loads, for the reason designPreview.ts gives:
// the public page's redirect to a video's canonical slug drops the query
// string before the player mounts.
const initialSearch = typeof window === 'undefined' ? '' : window.location.search

export const skinFromQuery = (): Skin => resolveSkin(new URLSearchParams(initialSearch).get('skin'))

// Gavin, 17 Sep: "Please avoid using Google fonts. I am using Arial here."
// Every skin — the font is not part of the paint.
export const PLAYER_FONT = 'Arial, Helvetica, sans-serif'
